import { useState, useEffect, useRef } from "preact/hooks";
import type { Setting, Texts, TrackingInfoData, TrackingInfoRequest } from "@/type";
import { getTrackPageData, queryTrackingInfo } from "@/network";
import { ORDER_NOT_FOUND_CODE, type ApiResponse } from "@/utils/http/types";
import SearchForm from "./components/SearchForm";
import OrderResult from "./components/OrderResult";
import {
  clearSearchQuery,
  getSearchQuery,
  getUrlSearchContext,
  saveSearchQuery,
  storedToRequest,
  type SearchQueryType,
  type StoredSearchQuery,
} from "@/utils/searchSession";

function QueryLoadingView() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
          className="animate-spin"
        >
          <g clip-path="url(#paint0_angular_997_4600_clip_path)" data-figma-skip-parse="true">
            <g transform="matrix(-0.006 -0.021 0.0196297 -0.00560849 24 24)">
              <foreignObject x="-1634.99" y="-1634.99" width="3269.97" height="3269.97">
                <div style="background:conic-gradient(from 90deg,rgba(0, 0, 0, 1) 0deg,rgba(0, 0, 0, 0) 91.875deg,rgba(0, 0, 0, 1) 360deg);height:100%;width:100%;opacity:1"></div>
              </foreignObject>
            </g>
          </g>
          <path
            d="M24 1.8C24 0.805886 23.193 -0.00702584 22.2017 0.0674564C18.0883 0.376513 14.1129 1.74178 10.6663 4.04473C6.71954 6.68188 3.64339 10.4302 1.82689 14.8156C0.0103866 19.201 -0.464894 24.0266 0.46115 28.6822C1.3872 33.3377 3.67298 37.6141 7.02943 40.9706C10.3859 44.327 14.6623 46.6128 19.3178 47.5388C23.9734 48.4649 28.799 47.9896 33.1844 46.1731C37.5698 44.3566 41.3181 41.2805 43.9553 37.3337C46.2582 33.8871 47.6235 29.9117 47.9325 25.7984C48.007 24.807 47.1941 24 46.2 24C45.2059 24 44.4082 24.8075 44.3206 25.7977C44.0198 29.1982 42.8692 32.4793 40.962 35.3336C38.7204 38.6884 35.5344 41.3031 31.8067 42.8471C28.0791 44.3912 23.9774 44.7952 20.0202 44.008C16.0629 43.2209 12.428 41.278 9.57502 38.425C6.72203 35.572 4.77912 31.9371 3.99198 27.9798C3.20484 24.0226 3.60883 19.9209 5.15285 16.1933C6.69688 12.4656 9.3116 9.2796 12.6664 7.03802C15.5207 5.13078 18.8018 3.98016 22.2023 3.67935C23.1925 3.59175 24 2.79411 24 1.8Z"
            data-figma-gradient-fill='{"type":"GRADIENT_ANGULAR","stops":[{"color":{"r":0.0,"g":0.0,"b":0.0,"a":1.0},"position":0.0},{"color":{"r":0.0,"g":0.0,"b":0.0,"a":0.0},"position":0.25520834326744080}],"stopsVar":[],"transform":{"m00":-11.999996185302734,"m01":39.259433746337891,"m02":10.370279312133789,"m10":-41.999996185302734,"m11":-11.216979026794434,"m12":50.608486175537109},"opacity":1.0,"blendMode":"NORMAL","visible":true}'
          />
          <defs>
            <clipPath id="paint0_angular_997_4600_clip_path">
              <path d="M24 1.8C24 0.805886 23.193 -0.00702584 22.2017 0.0674564C18.0883 0.376513 14.1129 1.74178 10.6663 4.04473C6.71954 6.68188 3.64339 10.4302 1.82689 14.8156C0.0103866 19.201 -0.464894 24.0266 0.46115 28.6822C1.3872 33.3377 3.67298 37.6141 7.02943 40.9706C10.3859 44.327 14.6623 46.6128 19.3178 47.5388C23.9734 48.4649 28.799 47.9896 33.1844 46.1731C37.5698 44.3566 41.3181 41.2805 43.9553 37.3337C46.2582 33.8871 47.6235 29.9117 47.9325 25.7984C48.007 24.807 47.1941 24 46.2 24C45.2059 24 44.4082 24.8075 44.3206 25.7977C44.0198 29.1982 42.8692 32.4793 40.962 35.3336C38.7204 38.6884 35.5344 41.3031 31.8067 42.8471C28.0791 44.3912 23.9774 44.7952 20.0202 44.008C16.0629 43.2209 12.428 41.278 9.57502 38.425C6.72203 35.572 4.77912 31.9371 3.99198 27.9798C3.20484 24.0226 3.60883 19.9209 5.15285 16.1933C6.69688 12.4656 9.3116 9.2796 12.6664 7.03802C15.5207 5.13078 18.8018 3.98016 22.2023 3.67935C23.1925 3.59175 24 2.79411 24 1.8Z" />
            </clipPath>
          </defs>
        </svg>

        {/* <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
        <p className="text-gray-500 text-sm">Loading...</p> */}
      </div>
    </div>
  );
}

export default function App() {
  const [settings, setSettings] = useState<Setting | null>(null);
  const [texts, setTexts] = useState<Texts | null>(null);
  const [initLoading, setInitLoading] = useState(true);

  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [orderNotFound, setOrderNotFound] = useState(false);
  const [orderResult, setOrderResult] = useState<TrackingInfoData | null>(null);

  const isApiResponse = (e: unknown): e is ApiResponse =>
    typeof e === "object" &&
    e !== null &&
    "code" in e &&
    typeof (e as ApiResponse).code === "number";

  const sessionRestoreAttempted = useRef(false);

  useEffect(() => {
    const init = async () => {
      try {
        const ctx = getUrlSearchContext();
        const res = await getTrackPageData({
          shop: ctx.shop,
          lang: ctx.lang,
          country: ctx.country,
        });

        setSettings(res.setting);
        setTexts(res.texts);

        if (res.setting.font_family.link) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = res.setting.font_family.link;
          document.head.appendChild(link);
        }

        document.title = res.setting.page_title;
      } catch {
        // 加载失败时不影响页面渲染，保持默认样式
      } finally {
        setInitLoading(false);
      }
    };
    init();
  }, []);

  // 页面背景色与背景图应用到 body
  useEffect(() => {
    if (!settings) return;

    const { style } = settings;
    const pageBg = style.colors.general_color.page_background;
    const imageUrl = style.background_image_url?.trim();

    document.body.style.backgroundColor = pageBg;
    document.body.style.minHeight = "100vh";

    if (imageUrl) {
      document.body.style.backgroundImage = `url("${imageUrl}")`;
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundRepeat = "no-repeat";
      document.body.style.backgroundPosition = "center";
    } else {
      document.body.style.backgroundImage = "";
      document.body.style.backgroundSize = "";
      document.body.style.backgroundRepeat = "";
      document.body.style.backgroundPosition = "";
    }

    return () => {
      document.body.style.backgroundColor = "";
      document.body.style.backgroundImage = "";
      document.body.style.backgroundSize = "";
      document.body.style.backgroundRepeat = "";
      document.body.style.backgroundPosition = "";
      document.body.style.minHeight = "";
    };
  }, [settings]);

  const runTrackingQuery = async (params: TrackingInfoRequest, type: SearchQueryType) => {
    const ctx = getUrlSearchContext();
    const fullParams: TrackingInfoRequest = { ...ctx, ...params };

    saveSearchQuery({
      type,
      shop: fullParams.shop,
      lang: fullParams.lang,
      country: fullParams.country,
      order: fullParams.order,
      email: fullParams.email,
      tracking_number: fullParams.tracking_number,
    });

    setQueryLoading(true);
    setQueryError(null);

    try {
      const result = await queryTrackingInfo(fullParams);
      setOrderResult(result);
      setOrderNotFound(false);
    } catch (e) {
      setOrderResult(null);
      if (isApiResponse(e) && (e.code === ORDER_NOT_FOUND_CODE || e.code === 7)) {
        setOrderNotFound(true);
        setQueryError(null);
      } else {
        setOrderNotFound(false);
        const fallback = texts?.order_not_found ?? "Order not found";
        setQueryError(
          isApiResponse(e) ? e.message || fallback : e instanceof Error ? e.message : fallback,
        );
      }
    } finally {
      setQueryLoading(false);
    }
  };

  const restoreFromSession = async (stored: StoredSearchQuery) => {
    const params = storedToRequest(stored);
    await runTrackingQuery(params, stored.type);
  };

  useEffect(() => {
    if (initLoading || !settings || !texts || sessionRestoreAttempted.current) return;
    sessionRestoreAttempted.current = true;

    const stored = getSearchQuery();
    if (stored) {
      restoreFromSession(stored);
    }
  }, [initLoading, settings, texts]);

  const handleSearchByOrder = (params: TrackingInfoRequest) => {
    runTrackingQuery(params, "order");
  };

  const handleSearchByTracking = (params: TrackingInfoRequest) => {
    runTrackingQuery(params, "tracking");
  };

  const handleBack = () => {
    clearSearchQuery();
    setOrderResult(null);
    setQueryError(null);
    setOrderNotFound(false);
  };

  if (initLoading) {
    return <QueryLoadingView />;
  }

  if (!settings || !texts) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Failed to load page configuration.</p>
      </div>
    );
  }

  return (
    <>
      {queryError && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-600 px-5 py-3 rounded-lg shadow-md text-sm">
          {queryError}
          <button className="ml-3 font-bold" onClick={() => setQueryError(null)}>
            ×
          </button>
        </div>
      )}

      {orderResult ? (
        <OrderResult
          settings={settings}
          texts={texts}
          shipments={orderResult.shipments}
          onBack={handleBack}
        />
      ) : (
        <SearchForm
          settings={settings}
          texts={texts}
          loading={queryLoading}
          orderNotFound={orderNotFound}
          onClearOrderNotFound={() => setOrderNotFound(false)}
          onSearchByOrder={handleSearchByOrder}
          onSearchByTracking={handleSearchByTracking}
        />
      )}
    </>
  );
}
